import Excel from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

import { sendRequest } from './global.js';

const STYLES = {
  header: {
    font: {
      bold: true,
      color: { argb: 'FF333333' },
      size: 11,
    },
    alignment: {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    },
  },
  cell_header: {
    font: {
      color: { argb: 'FF333333' },
      size: 11,
    },
    alignment: {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    },
    border: {
      top: { style: 'thin', color: { argb:'FF000000' } },
      left: { style: 'thin', color: { argb:'FF000000' } },
      bottom: { style: 'thin', color: { argb:'FF000000' } },
      right: { style: 'thin', color: { argb:'FF000000' } },
    },
  },
  cell_value: {
    font: {
      color: { argb: 'FF333333' },
      size: 11,
    },
    alignment: {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    },
    border: {
      top: { style: 'thin', color: { argb:'FF000000' } },
      left: { style: 'thin', color: { argb:'FF000000' } },
      bottom: { style: 'thin', color: { argb:'FF000000' } },
      right: { style: 'thin', color: { argb:'FF000000' } },
    },
  },
  footer: {
    font: {
      bold: true,
      color: { argb: 'FF333333' },
      size: 11,
    },
    alignment: {
      horizontal: 'left',
      vertical: 'middle',
      wrapText: true,
    },
  },
  sign: {
    font: {
      bold: true,
      color: { argb: 'FF333333' },
      size: 11,
    },
    alignment: {
      horizontal: 'left',
      vertical: 'bottom',
      wrapText: true,
    },
  },
}

const addText = (worksheet, row, col, text, type) => {
  const cell = worksheet.getRow(row).getCell(col);
  const style = STYLES[type] || {};
  ['font', 'alignment', 'border'].forEach(key => {
    if (style[key]) {
      cell[key] = style[key];
    }
  });
  cell.value = text;
}

const createWorkbook = () => {
  const workbook = new Excel.Workbook();
  const worksheet = workbook.addWorksheet('Акт приема-передачи', {
    pageSetup: { paperSize: 9, orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 9999 },
  });
  worksheet.columns = [...Array(13)].map((v, i) => (
    {
      width:
        i === 0 ? 5 :
        i === 8 ? 50 :
        i === 12 ? 45 :
        20 // pt
    }
  ));
  return ({ workbook, worksheet });
}

const saveWorkbook = async (workbook, title) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  saveAs(blob, `${title}.xlsx`);
}

const renderHeader = (worksheet, info, data) => {
  const last = () => worksheet.rowCount;
  const next = () => worksheet.rowCount + 1;
  const headerText = info.type === 'number'
    ? ((info.act === 'return'
        ? `Акт приема-возврата недоставленного почтового отправления №${info.number} за истечением срока хранения, переданного в доставку`
        : `Акт приема-передачи почтового отправления №${info.number}, переданного в доставку`
      ) +
      ` от ${info.userGroup.name}`)
    : ((info.act === 'return'
        ? 'Акт приема-возврата недоставленных почтовых отправлений за истечением срока хранения, переданных в доставку '
        : 'Акт приема-передачи почтовых отправлений, переданных в доставку '
      ) +
      (info.from === info.to ? info.from : `с ${info.from} по ${info.to}`) + 
      ` от ${info.userGroup.name}`)
  ;
  addText(worksheet, next(), 1, headerText, 'header');
  worksheet.mergeCells(`A${last()}:M${last()}`);
  worksheet.getRow(last()).height = 60;

  const tableHeaders = [
    '№ п/п',
    'Дата передачи письма оператору почтовой связи',
    'Идентификационный номер',
    'Получатель',
    'Индекс',
    'Населенный пункт',
    'Улица',
    'Номер дома и квартиры',
    'Отправитель, состав письма',
    'Вид корреспонденции',
    'Цена отправления, руб.',
    'Вес, г',
    'Статус на текущую дату',
  ];
  tableHeaders.forEach((text, i) => {
    addText(worksheet, i === 0 ? next() : last(), 1 + i, text, 'cell_header');
  });
}

const renderFooter = (worksheet, info, data, { kinds, totalPrice }) => {
  const last = () => worksheet.rowCount;
  const next = () => worksheet.rowCount + 1;

  addText(worksheet, next(), 1, '', 'footer');

  const totalString = Object.keys(kinds).map(key =>
    `${key || 'отправление без категории'} - ${kinds[key]}шт.`
  ).join(', ') + `; общей стоимостью ${totalPrice.toFixed(2)} руб.`;
  const footerText = `Итого ${info.userGroup.name} ` +
    'передано, а ООО "Принтекс" получено: ' +
    totalString
  ;
  addText(worksheet, next(), 1, footerText, 'footer');
  worksheet.mergeCells(`A${last()}:M${last()}`);
  worksheet.getRow(last()).height = 40;

  const signText1 = 'Заказчик_______________________';
  addText(worksheet, next(), 1, signText1, 'sign');
  worksheet.mergeCells(`A${last()}:C${last()}`);
  const signText2 = 'Исполнитель_______________________';
  addText(worksheet, last(), 7, signText2, 'sign');
  worksheet.mergeCells(`G${last()}:I${last()}`);
  worksheet.getRow(last()).height = 40;
}

const renderData = (worksheet, info, data) => {
  const last = () => worksheet.rowCount;
  const next = () => worksheet.rowCount + 1;

  let kinds = {};
  let totalPrice = 0;

  data.forEach((item, i) => {
    const type = item.fields.find(field => field.name === 'Вид');
    const category = item.fields.find(field => field.name === 'Категория');
    const price = item.fields.find(field => field.name === 'Стоимость отправления');
    const weight = item.fields.find(field => field.name === 'Вес');
    const kind = (type && type.value ? `${type.value} ` : '') + (category ? `${category.value || ''}` : '');

    let maxDate = null;
    let statusChanges = item.status_changes.reverse().map(change => {
      let updated = change;
      if (maxDate && change.create_time > maxDate) {
        updated.create_time = maxDate;
      } else {
        maxDate = updated.create_time;
      }
      return updated;
    }).reverse();

    const fixDate = dayjs('06.06.2021');
    if (dayjs(item.create_time, 'DD.MM.YYYY - HH:mm').diff(fixDate, 'hour') < 0) {
      statusChanges = statusChanges.sort((a, b) => {
        if (a.create_time > b.create_time) {
          return 1;
        } else if (a.create_time < b.create_time) {
          return -1;
        } else if (a.to === 1 && b.to !== 1) {
          return -1;
        } else if (b.to === 1 && a.to !== 1) {
          return 1;
        } else {
          return 0;
        }
      })
    }

    let statuses = statusChanges.map(change => {
      const status = info.statuses.find(i => i.id === change.to);
      if (!status) {
        return null;
      }
      const changeDate = dayjs(change.create_time);
      return `${status.name} (${changeDate.format('DD.MM.YYYY')})`;
    }).filter(i => i).join('\n');
    if (!statuses) {
      statuses = item.mailing_state_name;
    }

    const values = [
      i + 1,
      (item.create_time || '').substr(0, 10),
      item.id,
      item.recipient_name,
      item.recipient_postalcode,
      item.recipient_address.locality,
      item.recipient_address.street,
      item.recipient_address.other,
      item.sender_name,
      kind || '-',
      price ? Number(price.value) || 0 : 0,
      weight ? Number(weight.value) || 0 : 0,
      statuses,
    ];
    kinds[kind] = kinds[kind] ? kinds[kind] + 1 : 1;
    totalPrice += Number(price ? price.value : 0) || 0;
    values.forEach((value, j) => {
      addText(worksheet, j === 0 ? next() : last(), j + 1, value, 'cell_value');
    });
  });
  return {
    kinds,
    totalPrice,
  }
}

const formAct = (info, data) => {
  const { workbook, worksheet } = createWorkbook();

  renderHeader(worksheet, info, data);
  const summary = renderData(worksheet, info, data);
  renderFooter(worksheet, info, data, summary);

  const workbookName = info.type === 'number'
    ? `Акт приема-передачи недоставленного ${info.number}`
    : `Акт приема-передачи недоставленных ${info.from}${info.from === info.to ? '' : ` - ${info.to}`}`
  saveWorkbook(workbook, workbookName);
}

export const generateAct = (act, info, items) => {
  let statuses = null;
  let userGroup = null;
  const handle = () => {
    if (statuses && userGroup) {
      formAct({...info, userGroup, statuses, act}, items);
    }
  }
  sendRequest({
    method: 'mailing_states/all/',
    type: 'GET',
    success: (data) => {
      statuses = data;
      handle();
    },
    error: (xhr, status, err) => {
    }
  });
  sendRequest({
    method: 'users/current',
    type: 'GET',
    success: (data) => {
      userGroup = data.user_group;
      handle();
    },
    error: (xhr, status, err) => {
    }
  });
}


// WEBPACK FOOTER //
// ./src/ActGenerator.js